
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import MainLayout from '@/components/layout/MainLayout';
import { CalendarClock, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { getPostExpiryGraceHoursRemaining, isInPostExpiryGracePeriod } from '@/utils/SubscriptionRedirectHelper';

const ExpiredSubscriptionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscription, subscriptionPlans, refreshSubscription } = useSubscription();
  const [planType, setPlanType] = useState<string>('');
  const [graceHoursRemaining, setGraceHoursRemaining] = useState<number>(0);
  const [inGracePeriod, setInGracePeriod] = useState(false);
  
  useEffect(() => {
    // Get plan type from URL query param or detect from subscription context
    const params = new URLSearchParams(location.search);
    const expiredParam = params.get('expired');
    
    if (expiredParam) {
      setPlanType(expiredParam);
      setInGracePeriod(isInPostExpiryGracePeriod(expiredParam));
      setGraceHoursRemaining(getPostExpiryGraceHoursRemaining(expiredParam));
    } else {
      // Try to detect from subscription
      const currentPlan = subscription?.subscription?.plan?.toLowerCase() || '';
      const isTrial = subscription?.subscription?.is_trial || false;
      const isFreePlan = currentPlan === 'free' || currentPlan === 'starter';
      
      if (isTrial) {
        setPlanType('trial');
        setInGracePeriod(isInPostExpiryGracePeriod('trial'));
        setGraceHoursRemaining(getPostExpiryGraceHoursRemaining('trial'));
      } else if (isFreePlan) {
        setPlanType('free');
        setInGracePeriod(isInPostExpiryGracePeriod('free'));
        setGraceHoursRemaining(getPostExpiryGraceHoursRemaining('free'));
      } else {
        setPlanType('paid');
        setInGracePeriod(isInPostExpiryGracePeriod('paid'));
        setGraceHoursRemaining(getPostExpiryGraceHoursRemaining('paid'));
      }
    }
    
    // Refresh subscription status
    refreshSubscription();
  }, [location.search, subscription, refreshSubscription]);

  const planName = planType.charAt(0).toUpperCase() + planType.slice(1);
  const displayHours = Math.max(1, graceHoursRemaining);
  const displayTime = displayHours >= 24
    ? `${Math.floor(displayHours / 24)} days and ${displayHours % 24} hours`
    : `${displayHours} hours`;
  
  // Get recommended plan based on expired plan type
  const getRecommendedPlan = () => {
    if (planType === 'free' || planType === 'trial') {
      return subscriptionPlans.find(plan => plan.name.toLowerCase() === 'basic') || subscriptionPlans[0];
    }
    return subscriptionPlans.find(plan => plan.name.toLowerCase() === 'pro') || subscriptionPlans[0];
  };
  
  const recommendedPlan = getRecommendedPlan();
  
  return (
    <MainLayout>
      <div className="container max-w-4xl mx-auto py-12 px-4">
        <Card className={inGracePeriod ? "border-amber-300 shadow-lg" : "border-red-300 shadow-lg"}>
          <CardHeader className={inGracePeriod ? "bg-amber-50" : "bg-red-50"}>
            <div className="flex items-center mb-2">
              {inGracePeriod ? (
                <Clock className="h-6 w-6 text-amber-600 mr-2" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
              )}
              <CardTitle className={inGracePeriod ? "text-amber-800" : "text-red-800"}>
                {planName} Plan {inGracePeriod ? "Grace Period" : "Expired"}
              </CardTitle>
            </div>
            <CardDescription className={inGracePeriod ? "text-amber-700" : "text-red-700"}>
              {inGracePeriod
                ? `Your ${planName} plan has expired, but you're in a grace period with ${displayTime} remaining.`
                : `Your ${planName} plan has expired. Upgrade now to regain access to all features.`
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2 flex items-center">
                  <CalendarClock className="h-5 w-5 mr-2 text-gray-500" />
                  What happens next?
                </h3>
                <p className="text-gray-600">
                  {inGracePeriod
                    ? `You still have access to your account and features for ${displayTime}. After that, you'll need to upgrade to continue using premium features.`
                    : `You've lost access to premium features. To regain access, please choose a subscription plan below.`
                  }
                </p>
              </div>
              
              {recommendedPlan && (
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <h3 className="text-lg font-medium mb-2 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-indigo-600" />
                    Recommended plan for you
                  </h3>
                  
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      <h4 className="text-lg font-semibold">{recommendedPlan.name} Plan</h4>
                      <p className="text-gray-600 text-sm">{recommendedPlan.description}</p>
                      <p className="mt-1">
                        <span className="font-semibold">₹{recommendedPlan.price_monthly}</span> / month
                      </p>
                    </div>
                    <Button 
                      onClick={() => navigate(`/subscription/plans?recommended=${recommendedPlan.id}`)}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      Choose {recommendedPlan.name} Plan
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  onClick={() => navigate("/subscription/plans")}
                  variant="default"
                  className="flex-1"
                >
                  View All Plans
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="flex-1"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default ExpiredSubscriptionPage;
